/**
 * Payment analytics tab — volume by method + recent high-value transactions.
 *
 * Extracted from PaymentManager.tsx during the Maintainability Sprint
 * Task 1.7 follow-up squeeze (2026-04-19). Behavior is preserved
 * byte-for-byte. Uses the extracted formatters and the shared
 * AdminDataTable/EmptyState primitives.
 */

import { useCallback, useMemo } from "react";
import { AlertCircle, BarChart3 } from "lucide-react";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  usePaymentKPIs,
  usePaymentTransactions,
} from "@/hooks/admin/usePayments";
import { getMethodLabel } from "./constants";
import { formatFcfa, formatDate } from "./formatters";

export function AnalyticsTab() {
  const {
    data: kpis,
    isLoading,
    isError: isKpiError,
    refetch: refetchKpis,
  } = usePaymentKPIs();
  const {
    data: txData,
    isError: isTxError,
    refetch: refetchTx,
  } = usePaymentTransactions({ limit: 20 });
  const isError = isKpiError || isTxError;
  const retry = useCallback(() => {
    if (isKpiError) refetchKpis();
    if (isTxError) refetchTx();
  }, [isKpiError, isTxError, refetchKpis, refetchTx]);

  const payments = txData?.payments ?? [];

  // Volume by method derived from transactions
  const volumeByMethod = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const t of payments) {
      acc[t.method] = (acc[t.method] ?? 0) + t.amount_fcfa;
    }
    return acc;
  }, [payments]);

  return (
    <div className="space-y-6">
      {/* Error state */}
      {isError && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Impossible de charger les analytiques.</span>
          <button
            type="button"
            onClick={retry}
            className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Volume par méthode */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-stone-700">
          Volume par méthode de paiement
        </h3>
        <div className="overflow-hidden rounded-lg border border-stone-200">
          <table className="w-full text-sm">
            <thead className="border-b border-stone-200 bg-stone-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                  Méthode
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-stone-500">
                  Volume (FCFA)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={2} className="px-4 py-6 text-center text-stone-400">
                    Chargement…
                  </td>
                </tr>
              ) : (
                <>
                  <tr className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                        Wave
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-stone-900">
                      {formatFcfa(kpis?.wave_volume_fcfa ?? 0)}
                    </td>
                  </tr>
                  <tr className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                        Orange Money
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-stone-900">
                      {formatFcfa(kpis?.orange_money_volume_fcfa ?? 0)}
                    </td>
                  </tr>
                  <tr className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-700">
                        COD
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-stone-900">
                      {formatFcfa(kpis?.cod_volume_fcfa ?? 0)}
                    </td>
                  </tr>
                  {Object.entries(volumeByMethod)
                    .filter(
                      ([k]) =>
                        !["wave", "orange_money", "cash_on_delivery"].includes(
                          k,
                        ),
                    )
                    .map(([method, volume]) => (
                      <tr key={method} className="hover:bg-stone-50">
                        <td className="px-4 py-3">
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                            {getMethodLabel(method)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-stone-900">
                          {formatFcfa(volume)}
                        </td>
                      </tr>
                    ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent high-value transactions */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-stone-700">
          Top commandes récentes
        </h3>
        {payments.length === 0 ? (
          <EmptyState
            icon={<BarChart3 className="h-5 w-5" />}
            title="Aucune donnée"
            description="Les transactions apparaîtront ici."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-stone-200">
            <table className="w-full text-sm">
              <thead className="border-b border-stone-200 bg-stone-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                    Commande
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                    Client
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-stone-500">
                    Montant
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 bg-white">
                {[...payments]
                  .sort((a, b) => b.amount_fcfa - a.amount_fcfa)
                  .slice(0, 10)
                  .map((t) => (
                    <tr key={t.id} className="hover:bg-stone-50">
                      <td className="px-4 py-3 font-mono text-xs text-stone-700">
                        {t.order_ref}
                      </td>
                      <td className="px-4 py-3 text-stone-700">
                        {t.customer_name}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-stone-900">
                        {formatFcfa(t.amount_fcfa)}
                      </td>
                      <td className="px-4 py-3 text-stone-500">
                        {formatDate(t.created_at)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
